<template>
  <main-large-dialog
    :has-agree="false"
    :is-visible.sync="computedIsVisible"
    :title="$i18n.t('dialog.dmaZonesStatistics')"
    body-height="60vh"
    dialog-width="60%"
    disagreeText="dialog.close"
    no-paddings-x
    no-paddings-y
  >
    <template #title>
      <v-row no-gutters align="center">
        <v-col cols="auto">
          {{ $i18n.t('dialog.dmaZonesStatistics') }}
        </v-col>
        <v-col cols="auto" class="ml-2 title-info" v-if="dmaZonesStatisticsRangeObject">
          {{ $i18n.t('sidebar.statisticsForDrawedZoneDateRange', dmaZonesStatisticsRangeObject) }}
        </v-col>
      </v-row>
    </template>
    <template #body>
      <template>
        <grey-tabs v-model="currentStatisticsTab" :tabs="computedTabs" :show-arrows="false" fill-height fill-width>
          <v-tab-item>
            <v-col class="pa-0">
              <v-row no-gutters justify="center">
                <v-col cols="8">
                  <data-input v-model="chartStatistic" v-bind="selectInput" style="max-width: 300px" />
                </v-col>
              </v-row>
              <v-row no-gutters justify="center">
                <v-col cols="8">
                  <canvas id="dmaZonesChart"></canvas>
                </v-col>
              </v-row>
            </v-col>
          </v-tab-item>
          <v-tab-item>
            <v-col class="py-0 table-col">
              <v-data-table
                fixed-header
                disable-pagination
                hide-default-footer
                :headers="zoneStatisticsTableHeaders"
                :items="$_objectToArray(partitionByDma)"
                style="border: 1px solid rgba(0,0,0,0.12); border-radius: 0"
              >
                <template #item.dma_id="{item}">
                  <span style="font-weight: bold"> {{ item.dma_name }} </span>
                </template>
              </v-data-table>
            </v-col>
          </v-tab-item>
        </grey-tabs>
      </template>
    </template>
  </main-large-dialog>
</template>
<script>
import { get } from 'vuex-pathify';

import Chart from 'chart.js';
import DataInput from '@/components/DataInput';
import GreyTabs from '@/components/GreyTabs';
import MainLargeDialog from '@/components/MainLargeDialog';

export default {
  name: 'DmaZonesStatisticsModal',
  components: {
    DataInput,
    GreyTabs,
    MainLargeDialog,
  },
  props: {
    dmaZonesStatisticsRangeObject: {
      type: Object,
    },
    isDmaZonesStatisticsVisible: {
      type: Boolean,
      required: true,
    },
    zoneStatisticsItems: {
      type: Array,
      default: () => {
        return [];
      },
    },
  },
  data: vm => ({
    chartStatistic: '',
    currentStatisticsTab: 0,
    dmaZonesChart: undefined,
    tabs: [
      {
        text: vm.$i18n.t('dialog.body.chart'),
      },
      {
        text: vm.$i18n.t('dialog.body.table'),
      },
    ],
  }),
  computed: {
    partitionByDma: get('billings/partitionByDma'),
    computedIsVisible: {
      get() {
        return this.isDmaZonesStatisticsVisible;
      },
      set(nV) {
        this.$emit('update:isDmaZonesStatisticsVisible', nV);
      },
    },
    computedTabs() {
      return this.tabs.map(tab => {
        return { name: tab.text };
      });
    },
    selectInput() {
      return {
        dataType: { name: 'select' },
        items: this.zoneStatisticsItems.map(item => {
          return { value: item.name, text: item.straightText };
        }),
      };
    },
    zoneStatisticsTableHeaders() {
      const headers = this.zoneStatisticsItems.map(item => {
        return { value: item.name, text: item.straightText, align: 'center' };
      });
      headers.unshift({ value: 'dma_id', text: '', align: 'center', width: '100px', sortable: false });
      return headers;
    },
  },
  watch: {
    chartStatistic(nV) {
      this.toggleChart(nV);
    },
    isDmaZonesStatisticsVisible(nV) {
      if (nV) {
        setTimeout(() => {
          this.chartStatistic = this.selectInput.items[0].value;
        });
      }
    },
  },
  methods: {
    toggleChart(nV) {
      const partitionsArray = this.$_objectToArray(this.partitionByDma);
      if (partitionsArray.length < 1) {
        return;
      }
      const data = partitionsArray.map(partition => partition[nV]);
      const labels = partitionsArray.map(partition => partition.dma_name);
      this.drawChart(labels, data);
    },
    drawChart(labels, data) {
      if (this.dmaZonesChart) {
        this.dmaZonesChart.destroy();
      }
      const canvas = document.getElementById('dmaZonesChart');
      this.dmaZonesChart = new Chart(canvas, {
        type: 'bar',
        data: {
          labels: labels,
          datasets: [
            {
              data: data,
              fill: false,
              lineTension: 1,
              borderColor: '#2a579a',
              backgroundColor: '#2a579a',
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          legend: {
            display: false,
          },
          elements: {
            point: {
              radius: 0,
            },
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  min: 0,
                },
              },
            ],
          },
        },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.table-col {
  display: flex;
  max-height: 100%;
}
.title-info {
  font-family: Roboto;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.59);
}
::v-deep {
  table > tbody > tr > td:nth-child(1),
  table > thead > tr > th:nth-child(1) {
    position: sticky !important;
    position: -webkit-sticky !important;
    left: 0;
    z-index: 9998;
    background-color: white;
    opacity: 1;
  }
  table > thead > tr > th:nth-child(1) {
    z-index: 9999;
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
  table > tbody > tr > td:nth-child(1) {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
  .v-tabs {
    z-index: 99;
    position: sticky;
    top: 0;
  }
  .v-tabs-items {
    height: calc(100% - 48px);
    overflow: auto;
  }
  .v-slide-group {
    box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12) !important;
  }
  .theme--light.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
    white-space: nowrap;
  }
  .v-window__container,
  .v-window-item {
    height: 100%;
  }
  .v-data-table__wrapper {
    overflow-y: auto;
    overflow-x: auto;
    max-height: 100%;
  }
}
</style>
